import { useState, useEffect } from 'react';
import debounce from 'lodash.debounce';

/**
 * useMediaQuery
 * @description Returns a boolean what tells if given media query is matching the current screen size
 * @param {MediaQueryType} query - Media query as a string, for the correct value see MediaQueryType
 * @return {boolean}
 *
 * Example:
 *   import { breakpoints } from '@/constants/breakpoints';
 *
 *   const isDesktop = useMediaQuery(`(min-width: ${breakpoints.DESKTOP}px)`);
 *
 *   Result: isDesktop = true | false
 */

const useMediaQuery = (query: MediaQueryType): boolean => {
  const [matches, setMatches] = useState(true);

  useEffect(() => {
    const media = window.matchMedia(query);

    if (media.matches !== matches) {
      setMatches(media.matches);
    }

    const resizeListener = debounce(() => setMatches(media.matches), 300);

    window.addEventListener('resize', resizeListener);

    return () => window.removeEventListener('resize', resizeListener);
  }, [matches, query]);

  return matches;
};

export default useMediaQuery;
